import React, { useState, ReactNode, Dispatch, SetStateAction } from 'react';
import { toast } from 'react-toastify';

import { createContext, useContext } from 'use-context-selector';
import { Unit } from '../components/ServiceItem';
import { convertRichUnitToUnit, RichUnit } from '../models/AttendanceUnit';
import { ProductItemProps } from '../models/Product';
import { Resources } from '../models/ScheduleResource';
import { ServiceItemProps } from '../models/Service';
import { fetchBranches } from '../modules/attendanceUnits';
import { fetchActiveServices } from '../modules/products';

export type ItemToSchedule = 'PRODUCT_ENV' | 'SERVICE_ENV' | 'SERVICE' | 'PRODUCT' | 'SPECIALIST' | null;

export enum ItemToScheduleType {
  PRODUCT_ENV = 'PRODUCT_ENV',
  SERVICE_ENV = 'SERVICE_ENV',
  SERVICE = 'SERVICE',
  PRODUCT = 'PRODUCT',
  SPECIALIST = 'SPECIALIST'
}

export type ModuleContextProps = {
  loading: boolean;
  units: Unit[] | undefined;
  selectedProduct?: ProductItemProps;
  selectedService?: ServiceItemProps;
  activeServices?: Resources[];
  selectedItemToSchedule: ItemToSchedule;
  selectItemToSchedule: Dispatch<SetStateAction<ItemToSchedule>>;
  getActiveServices: (searchTerm: string) => Promise<void>;
  getAttendanceUnits: () => void;
  handleSelectProduct: (product: ProductItemProps) => void;
  handleSelectService: (service: ServiceItemProps) => void;
  logged: boolean;
  setLogged: React.Dispatch<React.SetStateAction<boolean>>;
  sdkToken: string;
  setSdkToken: React.Dispatch<React.SetStateAction<string>>;
};

type ModuleProviderProps = {
  children: ReactNode;
};

export const ModuleContext = createContext({} as ModuleContextProps);

export function ModuleProvider({ children }: ModuleProviderProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductItemProps>();
  const [selectedService, setSelectedService] = useState<ServiceItemProps>();
  const [activeServices, setActiveServices] = useState<Resources[]>();
  const [units, setUnits] = useState<Unit[] | undefined>([]);
  const [logged, setLogged] = React.useState(false);
  const [sdkToken, setSdkToken] = React.useState('');

  const [loadingActiveServices, setLoadingActiveServices] = useState(false);

  const loading = loadingActiveServices;

  const [selectedItemToSchedule, selectItemToSchedule] = useState<ItemToSchedule>(null);

  const handleSelectProduct = (product: ProductItemProps) => {
    setSelectedProduct(product);
    setSelectedService(undefined);
  };

  const handleSelectService = (service: ServiceItemProps) => {
    setSelectedProduct(undefined);
    setSelectedService(service);
  };

  const getActiveServices = async (searchTerm: string) => {
    setLoadingActiveServices(true);
    const result = await fetchActiveServices(searchTerm);
    const { error, data } = result;
    if (!error) {
      setActiveServices(data);
    }
    if (error) {
      toast.error('Erro ao obter produtos.');
      setActiveServices(undefined);
    }
    setLoadingActiveServices(false);
  };

  const getAttendanceUnits = async () => {
    const { data } = await fetchBranches(30);
    const units = data.map((richUnit: RichUnit) => convertRichUnitToUnit(richUnit));
    setUnits(units);
  };

  return (
    <ModuleContext.Provider
      value={{
        loading,
        units,
        selectedProduct,
        selectedService,
        activeServices,
        selectedItemToSchedule,
        selectItemToSchedule,
        getActiveServices,
        getAttendanceUnits,
        handleSelectProduct,
        handleSelectService,
        logged,
        setLogged,
        sdkToken,
        setSdkToken
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
}

export function useModuleContext() {
  return useContext(ModuleContext);
}
